import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import Footer from '@/components/Footer'
import { useLanguage } from '@/localization'
import type { CommunityData } from '@/useCommunity'

/**
 * Shared chrome + typographic primitives for the platform-wide legal documents
 * (Privacy Policy, Terms of Service). These live on the hub as the single
 * canonical source; every game site (KF1, Minecraft, …) links up to them rather
 * than shipping its own copy. Game-specific clauses are expressed inline as
 * <GameNote> blocks so one document covers the whole platform without drift.
 */
export default function LegalDoc({
  eyebrow,
  title,
  accent,
  lastUpdated,
  intro,
  social,
  children,
}: {
  eyebrow: string
  /** Leading part of the heading rendered in the metal gradient. */
  title: string
  /** Trailing word rendered in the brand red (e.g. "Policy", "Service"). */
  accent: string
  lastUpdated: string
  intro: ReactNode
  social: CommunityData['social']
  children: ReactNode
}) {
  const { t } = useLanguage()

  // Land at the top when navigating in from a footer link on any site.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="flex min-h-screen flex-col">
      <main className="mx-auto w-full max-w-4xl flex-1 px-6 py-16 sm:py-20">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 font-rajdhani text-sm font-semibold text-stone-500 transition-colors hover:text-uliunai-red"
        >
          <ArrowLeft size={16} />
          {t('Back to the hub')}
        </Link>

        <header className="mt-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-stone-line bg-white px-4 py-1.5 font-rajdhani text-xs font-semibold uppercase tracking-[0.25em] text-uliunai-red shadow-card">
            {t(eyebrow)}
          </span>
          <h1 className="metal-text mt-5 font-orbitron text-4xl font-black tracking-tight sm:text-5xl">
            {t(title)} <span className="text-uliunai-red">{t(accent)}</span>
          </h1>
          <p className="mt-3 font-rajdhani text-sm text-stone-500">
            {t('Last updated:')} {lastUpdated}
          </p>
          <div className="mt-6 space-y-4 font-rajdhani text-base leading-relaxed text-stone-600">
            {intro}
          </div>
        </header>

        <div className="mt-12 space-y-6">{children}</div>

        <p className="mt-10 text-center font-rajdhani text-sm text-stone-500">
          {t('Questions about this document? Reach an admin on')}{' '}
          <a href="/#discord" className="font-semibold text-uliunai-red hover:underline">
            Discord
          </a>{' '}
          {t('or via the')}{' '}
          <a href="/#contact" className="font-semibold text-uliunai-red hover:underline">
            {t('contact form')}
          </a>
          .
        </p>
      </main>

      <Footer social={social} />
    </div>
  )
}

/** A titled card section, matching the ConductPage rhythm. */
export function Section({ title, children }: { title: string; children: ReactNode }) {
  const { t } = useLanguage()

  return (
    <section className="rounded-2xl border border-stone-line bg-white p-7 shadow-card sm:p-8">
      <h2 className="font-orbitron text-xl font-bold text-stone-900 sm:text-2xl">{t(title)}</h2>
      <div className="mt-4 space-y-4 font-rajdhani text-base leading-relaxed text-stone-700">
        {children}
      </div>
    </section>
  )
}

/** A sub-heading within a section. */
export function SubHead({ children }: { children: ReactNode }) {
  const { t } = useLanguage()
  return (
    <h3 className="mt-2 font-orbitron text-base font-bold text-stone-900">
      {typeof children === 'string' ? t(children) : children}
    </h3>
  )
}

/** A bulleted list of plain items or rich nodes. */
export function List({ items }: { items: ReactNode[] }) {
  const { t } = useLanguage()
  return (
    <ul className="space-y-2 pl-5">
      {items.map((item, i) => (
        <li key={i} className="list-disc text-stone-700 marker:text-uliunai-red">
          {typeof item === 'string' ? t(item) : item}
        </li>
      ))}
    </ul>
  )
}

/** A highlighted, game-scoped clause that only applies to one game's site. */
export function GameNote({ game, children }: { game: string; children: ReactNode }) {
  const { t } = useLanguage()
  return (
    <div className="rounded-xl border border-stone-line bg-parchment/60 p-5">
      <span className="inline-flex items-center rounded-full bg-uliunai-red/10 px-3 py-1 font-rajdhani text-xs font-bold uppercase tracking-wider text-uliunai-red">
        {t(game)}
      </span>
      <div className="mt-3 space-y-3">{children}</div>
    </div>
  )
}
